export const REDACTED_PLACEHOLDER = "******";

export type BioSegment = { kind: "text"; value: string } | { kind: "redacted" };

interface RedactionRange {
  start: number;
  end: number;
}

const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/gi;

const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

const BARE_DOMAIN_REGEX =
  /(?<![@\w./])(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+(?:com|org|net|io|fr|ca|co|dev|app|me|info|biz|tv|us|uk|de|eu|tech|online|site|shop|store|blog|link|pro|agency|studio|design|media|cloud|ai|xyz|quebec|gouv)(?:\/[^\s]*)?(?![@\w])/gi;

const PHONE_CANDIDATE_REGEX =
  /(?:\+?\d{1,3}[\s.-]?)?(?:\(?\d{2,4}\)?[\s.-]?)?(?:\d[\s.-]?){5,12}\d/g;

const YEAR_RANGE_PATTERN = /^\d{4}\s*[-–—]\s*\d{4}$/;
const ISO_DATE_PATTERN = /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$/;
const DAY_MONTH_YEAR_PATTERN = /^\d{1,2}[-/.]\d{1,2}[-/.]\d{4}$/;
const NORTH_AMERICAN_PHONE_PATTERN = /\d{3}[-.\s]\d{3}[-.\s]\d{4}/;

function isLikelyDateLikePattern(value: string): boolean {
  const trimmed = value.trim();

  return (
    YEAR_RANGE_PATTERN.test(trimmed) ||
    ISO_DATE_PATTERN.test(trimmed) ||
    DAY_MONTH_YEAR_PATTERN.test(trimmed)
  );
}

function hasPhoneFormatting(value: string, digits: string): boolean {
  const trimmed = value.trim();

  return (
    trimmed.startsWith("+") ||
    /\(\d{2,4}\)/.test(trimmed) ||
    NORTH_AMERICAN_PHONE_PATTERN.test(trimmed) ||
    /^0[1-9]/.test(digits) ||
    (digits.length >= 10 && /[\s.-]/.test(trimmed))
  );
}

function isLikelyPhone(value: string): boolean {
  const trimmed = value.trim();

  if (isLikelyDateLikePattern(trimmed)) {
    return false;
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) {
    return false;
  }

  return hasPhoneFormatting(trimmed, digits);
}

function findRanges(
  text: string,
  regex: RegExp,
  validate?: (match: string) => boolean,
): RedactionRange[] {
  const ranges: RedactionRange[] = [];
  const pattern = new RegExp(regex.source, regex.flags);

  for (const match of text.matchAll(pattern)) {
    const value = match[0];
    const start = match.index ?? 0;

    if (validate && !validate(value)) {
      continue;
    }

    ranges.push({ start, end: start + value.length });
  }

  return ranges;
}

function mergeRanges(ranges: RedactionRange[]): RedactionRange[] {
  if (ranges.length === 0) {
    return [];
  }

  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: RedactionRange[] = [sorted[0]];

  for (let index = 1; index < sorted.length; index += 1) {
    const current = sorted[index];
    const last = merged[merged.length - 1];

    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
      continue;
    }

    merged.push(current);
  }

  return merged;
}

export function parseBioSegments(text: string): BioSegment[] {
  if (!text) {
    return [];
  }

  const ranges = mergeRanges([
    ...findRanges(text, EMAIL_REGEX),
    ...findRanges(text, URL_REGEX),
    ...findRanges(text, BARE_DOMAIN_REGEX),
    ...findRanges(text, PHONE_CANDIDATE_REGEX, isLikelyPhone),
  ]);

  if (ranges.length === 0) {
    return [{ kind: "text", value: text }];
  }

  const segments: BioSegment[] = [];
  let cursor = 0;

  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({ kind: "text", value: text.slice(cursor, range.start) });
    }

    segments.push({ kind: "redacted" });
    cursor = range.end;
  }

  if (cursor < text.length) {
    segments.push({ kind: "text", value: text.slice(cursor) });
  }

  return segments;
}

export function redactPersonalInfo(text: string): string {
  return parseBioSegments(text)
    .map((segment) =>
      segment.kind === "redacted" ? REDACTED_PLACEHOLDER : segment.value,
    )
    .join("");
}
